import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import { Navigation } from '../components/Navigation';
import {
  Upload, FileSpreadsheet, TrendingUp, AlertTriangle, Receipt,
  Search, Download, Sparkles, Trash2, X, ChevronDown,
  Filter, RefreshCw, BadgeCheck,
} from 'lucide-react';

const API = '/expenses';

const fmtAmount = (v) => {
  if (v === null || v === undefined) return '';
  const num = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(num)) return '';
  return num.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtAmountShort = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + ' mio.';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + 'k';
  return Math.round(n).toString();
};

// ============ CARDS ============
const KpiCard = ({ label, value, sublabel, accent = '#1E6F7A', icon: Icon }) => (
  <div data-testid={`expense-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`} className="bg-[#13151a] border border-white/[0.06] rounded-xl px-5 py-4 hover:border-white/[0.12] transition-colors">
    <div className="flex items-center justify-between mb-2.5">
      <span className="text-[10px] uppercase tracking-[0.15em] text-white/35 font-semibold">{label}</span>
      {Icon && <Icon className="w-4 h-4" style={{ color: accent }} />}
    </div>
    <div className="text-2xl font-bold tabular-nums" style={{ color: accent }}>{value}</div>
    {sublabel && <div className="text-[11px] text-white/40 mt-1.5">{sublabel}</div>}
  </div>
);

// ============ UPLOAD ZONE ============
const UploadZone = ({ onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post(`${API}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ type: 'ok', text: `${res.data.imported} nye transaktioner importeret. ${res.data.skipped_duplicates} dubletter sprunget over (ud af ${res.data.total_in_file} i filen).` });
      onUploaded?.();
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Importfejl' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-6">
      <div
        data-testid="expense-upload-zone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        className="border-2 border-dashed border-white/[0.08] hover:border-[#1E6F7A]/50 rounded-lg px-6 py-8 text-center cursor-pointer transition-colors"
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e) => handleFile(e.target.files[0])} data-testid="expense-upload-input" />
        <Upload className="w-10 h-10 mx-auto text-white/30 mb-3" />
        <p className="text-sm text-white/70 font-medium">Træk CSV/Excel-fil hertil — eller klik for at vælge</p>
        <p className="text-[11px] text-white/35 mt-1">Understøtter Nordea, Danske Bank, Jyske Bank m.fl.</p>
        {busy && <p className="text-xs text-[#1E6F7A] mt-3 animate-pulse">Importerer…</p>}
        {msg && <p className={`text-xs mt-3 ${msg.type === 'ok' ? 'text-emerald-400' : 'text-rose-400'}`}>{msg.text}</p>}
      </div>
    </div>
  );
};

// ============ FILTER BAR ============
const FilterBar = ({ filters, setFilters, categories, responsibles, vendors }) => {
  const upd = (k, v) => setFilters(prev => ({ ...prev, [k]: v || null }));
  return (
    <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-4 flex flex-wrap gap-2 items-center" data-testid="expense-filter-bar">
      <Filter className="w-4 h-4 text-white/40" />
      <input data-testid="filter-search" type="text" placeholder="Søg tekst/leverandør…" value={filters.search || ''} onChange={(e) => upd('search', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/30 w-56 focus:outline-none focus:border-[#1E6F7A]/50" />
      <input data-testid="filter-date-from" type="date" value={filters.date_from || ''} onChange={(e) => upd('date_from', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50" />
      <span className="text-white/30 text-xs">→</span>
      <input data-testid="filter-date-to" type="date" value={filters.date_to || ''} onChange={(e) => upd('date_to', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50" />
      <select data-testid="filter-category" value={filters.category || ''} onChange={(e) => upd('category', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50">
        <option value="">Alle kategorier</option>
        {categories.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select data-testid="filter-vendor" value={filters.vendor || ''} onChange={(e) => upd('vendor', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50">
        <option value="">Alle leverandører</option>
        {vendors.slice(0, 100).map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <select data-testid="filter-responsible" value={filters.responsible || ''} onChange={(e) => upd('responsible', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50">
        <option value="">Alle ansvarlige</option>
        {responsibles.map(u => <option key={u.email} value={u.email}>{u.email}</option>)}
      </select>
      <select data-testid="filter-action" value={filters.action || ''} onChange={(e) => upd('action', e.target.value)}
        className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#1E6F7A]/50">
        <option value="">Alle handlinger</option>
        <option value="Behold">Behold</option>
        <option value="Opsig">Opsig</option>
        <option value="Forhandl pris">Forhandl pris</option>
        <option value="Undersøg nærmere">Undersøg nærmere</option>
        <option value="Mangler ansvarlig">Mangler ansvarlig</option>
      </select>
      <button data-testid="clear-filters" onClick={() => setFilters({})} className="px-3 py-1.5 text-xs text-white/50 hover:text-white/80 hover:bg-white/[0.04] rounded-lg transition-colors">
        <X className="w-3.5 h-3.5 inline" /> Nulstil
      </button>
    </div>
  );
};

// ============ TRANSACTION ROW (with inline editing) ============
const TxRow = ({ tx, categories, responsibles, onChange }) => {
  const [editing, setEditing] = useState(false);
  const isExpense = tx.amount < 0;
  return (
    <div data-testid={`tx-row-${tx.id}`} className="grid items-center text-xs border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
      style={{ gridTemplateColumns: '100px 1fr 180px 120px 110px 130px 160px 30px' }}>
      <div className="px-3 py-2 text-white/50">{tx.date}</div>
      <div className="px-3 py-2 text-white/80 truncate" title={tx.text}>{tx.text}</div>
      {editing ? (
        <input type="text" defaultValue={tx.vendor || ''} onBlur={(e) => { onChange({ vendor: e.target.value }); setEditing(false); }} autoFocus
          className="mx-3 my-1 bg-white/[0.06] border border-[#1E6F7A]/40 rounded px-2 py-1 text-xs text-white" />
      ) : (
        <div className="px-3 py-2 text-white/70 truncate cursor-pointer hover:text-white" onClick={() => setEditing(true)} title={tx.vendor}>{tx.vendor || <span className="text-white/30">—</span>}</div>
      )}
      <select data-testid={`tx-category-${tx.id}`} value={tx.category || ''} onChange={(e) => onChange({ category: e.target.value })}
        className="mx-2 my-0.5 bg-transparent border border-transparent hover:border-white/10 focus:border-[#1E6F7A]/50 rounded px-2 py-1 text-xs text-white/70 focus:outline-none">
        <option value="">—</option>
        {categories.map(c => <option key={c} value={c} className="bg-[#1a1a1a]">{c}</option>)}
      </select>
      <div className={`px-3 py-2 text-right tabular-nums font-semibold ${isExpense ? 'text-rose-400/85' : 'text-emerald-400/90'}`}>{fmtAmount(tx.amount)}</div>
      <select data-testid={`tx-action-${tx.id}`} value={tx.action || ''} onChange={(e) => onChange({ action: e.target.value })}
        className="mx-2 my-0.5 bg-transparent border border-transparent hover:border-white/10 focus:border-[#1E6F7A]/50 rounded px-2 py-1 text-xs text-white/70 focus:outline-none">
        <option value="">—</option>
        <option value="Behold">Behold</option>
        <option value="Opsig">Opsig</option>
        <option value="Forhandl pris">Forhandl pris</option>
        <option value="Undersøg nærmere">Undersøg nærmere</option>
        <option value="Mangler ansvarlig">Mangler ansvarlig</option>
      </select>
      <select data-testid={`tx-responsible-${tx.id}`} value={tx.responsible_email || ''} onChange={(e) => onChange({ responsible_email: e.target.value })}
        className="mx-2 my-0.5 bg-transparent border border-transparent hover:border-white/10 focus:border-[#1E6F7A]/50 rounded px-2 py-1 text-xs text-white/70 focus:outline-none">
        <option value="">—</option>
        {responsibles.map(u => <option key={u.email} value={u.email} className="bg-[#1a1a1a]">{u.email}</option>)}
      </select>
      <button data-testid={`tx-delete-${tx.id}`} onClick={() => { if (window.confirm('Skjul denne transaktion?')) onChange({ hidden: true }); }} className="text-white/20 hover:text-rose-400 px-2">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
};

// ============ TABS ============
const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'transactions', label: 'Transaktioner' },
  { key: 'subscriptions', label: 'Abonnementer' },
  { key: 'duplicates', label: 'Dubletter' },
  { key: 'ai', label: 'AI-forslag' },
];

// ============ DECISION COLOR HELPERS ============
const DECISION_COLORS = {
  'Behold': { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/40' },
  'Opsig': { bg: 'bg-rose-500/15', text: 'text-rose-400', border: 'border-rose-500/40' },
  'Forhandl pris': { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/40' },
  'Undersøg nærmere': { bg: 'bg-sky-500/15', text: 'text-sky-400', border: 'border-sky-500/40' },
  'Skjul': { bg: 'bg-white/5', text: 'text-white/40', border: 'border-white/10' },
};

// ============ SUBSCRIPTIONS PANEL ============
const SubscriptionsPanel = ({ dashboard, responsibles, onChange }) => {
  const [hideReviewed, setHideReviewed] = useState(true);
  const [hideOk, setHideOk] = useState(false);
  const [search, setSearch] = useState('');

  const setDecision = async (vendor, decision) => {
    try {
      await api.put(`${API}/vendor-decisions/${encodeURIComponent(vendor)}`, { decision: decision || '' });
      onChange?.();
    } catch (_) {}
  };

  const visible = useMemo(() => {
    return (dashboard.subscriptions || []).filter(s => {
      if (hideReviewed && s.decision) return false;
      if (hideOk && !s.cadence && !s.rising && !s.inactive) return false;
      if (search && !s.vendor.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [dashboard, hideReviewed, hideOk, search]);

  const totalCount = (dashboard.subscriptions || []).length;
  const reviewedCount = (dashboard.subscriptions || []).filter(s => s.decision).length;
  const remaining = totalCount - reviewedCount;
  const totalRemainingAmount = visible.reduce((sum, s) => sum + (s.total_amount || 0), 0);

  return (
    <div className="space-y-3">
      {/* Toolbar med stats og toggles */}
      <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid="subs-toolbar">
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-white/90 tabular-nums">{remaining}</span>
          <span className="text-xs text-white/50">til gennemgang</span>
          <span className="text-[11px] text-white/30 ml-1.5">({reviewedCount}/{totalCount} klaret)</span>
        </div>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-baseline gap-1.5">
          <span className="text-sm font-semibold text-rose-400/85 tabular-nums">{fmtAmount(totalRemainingAmount)} kr</span>
          <span className="text-xs text-white/50">i synligt scope</span>
        </div>
        <div className="flex-1" />
        <input data-testid="subs-search" type="text" placeholder="Søg leverandør…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/30 w-48 focus:outline-none focus:border-[#1E6F7A]/50" />
        <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer select-none" data-testid="toggle-hide-reviewed">
          <input type="checkbox" checked={hideReviewed} onChange={(e) => setHideReviewed(e.target.checked)} className="accent-[#1E6F7A]" />
          Skjul gennemgåede
        </label>
        <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer select-none" data-testid="toggle-hide-ok">
          <input type="checkbox" checked={hideOk} onChange={(e) => setHideOk(e.target.checked)} className="accent-[#1E6F7A]" />
          Skjul ikke-abonnementer
        </label>
      </div>

      {/* Subs liste */}
      <div className="bg-[#13151a] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold">Abonnementer & gentagne betalinger</h3>
          <span className="text-[11px] text-white/40">{visible.length} synlige</span>
        </div>
        <div className="grid items-center text-[10px] uppercase tracking-wider text-white/35 font-semibold bg-white/[0.02] py-2.5"
          style={{ gridTemplateColumns: '1fr 95px 100px 90px 60px 100px 110px 150px' }}>
          <div className="px-3">Leverandør</div>
          <div className="px-3 text-right">Snitbeløb</div>
          <div className="px-3 text-right">Total</div>
          <div className="px-3">Kadence</div>
          <div className="px-3 text-center">Antal</div>
          <div className="px-3">Sidst</div>
          <div className="px-3">Flag</div>
          <div className="px-3">Beslutning</div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="subs-list">
          {visible.length === 0 ? (
            <div className="p-8 text-center text-emerald-400/70 text-sm">
              {totalCount > 0 ? '🎯 Du er igennem hele listen — alt er gennemgået!' : 'Ingen abonnementer fundet endnu. Importér en CSV-fil ovenfor.'}
            </div>
          ) : visible.map(s => {
            const dc = s.decision ? DECISION_COLORS[s.decision] : null;
            return (
              <div key={s.vendor} className={`grid items-center text-xs border-t border-white/[0.04] hover:bg-white/[0.02] py-1.5 ${dc ? `border-l-2 ${dc.border}` : ''}`}
                style={{ gridTemplateColumns: '1fr 95px 100px 90px 60px 100px 110px 150px' }}>
                <div className="px-3 text-white/85 truncate" title={s.vendor}>{s.vendor}</div>
                <div className="px-3 text-right text-rose-300/80 tabular-nums">{fmtAmount(s.avg_amount)}</div>
                <div className="px-3 text-right text-rose-400/85 font-semibold tabular-nums">{fmtAmount(s.total_amount)}</div>
                <div className="px-3 text-white/60">{s.cadence || <span className="text-white/25">—</span>}</div>
                <div className="px-3 text-center text-white/60">{s.count}</div>
                <div className="px-3 text-white/50">{s.last_date}</div>
                <div className="px-3 flex gap-1 flex-wrap">
                  {s.rising && <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Stigende</span>}
                  {s.inactive && <span className="text-[9px] bg-rose-500/20 text-rose-400 px-1.5 py-0.5 rounded">Inaktiv</span>}
                  {!s.rising && !s.inactive && s.cadence && <span className="text-[9px] bg-emerald-500/15 text-emerald-400/80 px-1.5 py-0.5 rounded">OK</span>}
                </div>
                <div className="px-2">
                  <select data-testid={`decision-${s.vendor}`} value={s.decision || ''} onChange={(e) => setDecision(s.vendor, e.target.value)}
                    className={`w-full bg-white/[0.04] border rounded px-2 py-1 text-[11px] focus:outline-none ${dc ? `${dc.text} ${dc.border}` : 'text-white/60 border-white/10 hover:border-white/30'}`}>
                    <option value="" className="bg-[#1a1a1a]">— Vælg —</option>
                    <option value="Behold" className="bg-[#1a1a1a]">✓ Behold</option>
                    <option value="Opsig" className="bg-[#1a1a1a]">✗ Opsig</option>
                    <option value="Forhandl pris" className="bg-[#1a1a1a]">⇣ Forhandl pris</option>
                    <option value="Undersøg nærmere" className="bg-[#1a1a1a]">? Undersøg</option>
                    <option value="Skjul" className="bg-[#1a1a1a]">— Skjul (ikke relevant)</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
export default function UdgiftsAnalyse() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [txs, setTxs] = useState([]);
  const [filters, setFilters] = useState({});
  const [categories, setCategories] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCategories = async () => {
    try {
      const [cRes, rRes] = await Promise.all([
        api.get(`${API}/categories`),
        api.get(`${API}/responsibles`),
      ]);
      setCategories(cRes.data.categories || []);
      setResponsibles(rRes.data.users || []);
    } catch (_) {}
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/dashboard`);
      setDashboard(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    const params = { ...filters };
    Object.keys(params).forEach(k => (params[k] === null || params[k] === undefined || params[k] === '') && delete params[k]);
    try {
      const res = await api.get(`${API}/transactions`, { params });
      setTxs(res.data.transactions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { if (tab === 'dashboard') loadDashboard(); }, [tab]);
  useEffect(() => { if (tab === 'transactions') loadTransactions(); }, [tab, filters]);

  const handleTxUpdate = async (txId, payload) => {
    setTxs(prev => prev.map(t => t.id === txId ? { ...t, ...payload } : t));
    try { await api.patch(`${API}/transactions/${txId}`, payload); }
    catch (_) { loadTransactions(); }
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const res = await api.post(`${API}/ai-analysis`, {});
      setAiResult(res.data);
    } catch (e) {
      setAiResult({ ok: false, error: e?.response?.data?.detail || 'AI-fejl' });
    } finally {
      setAiBusy(false);
    }
  };

  const downloadExcel = () => {
    const params = new URLSearchParams();
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.category) params.set('category', filters.category);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${API}/export/excel?${params.toString()}`;
    window.open(url, '_blank');
  };

  const vendorList = useMemo(() => {
    const set = new Set();
    txs.forEach(t => t.vendor && set.add(t.vendor));
    if (dashboard?.top_vendors) dashboard.top_vendors.forEach(v => set.add(v.vendor));
    return Array.from(set).sort();
  }, [txs, dashboard]);

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="expense-page">
      <header className="bg-[#111318] border-b border-white/[0.06] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Navigation />
            <div>
              <h1 className="text-base font-semibold text-white flex items-center gap-2.5">
                <Receipt className="w-4 h-4 text-[#1E6F7A]" />
                Udgifts Analyse
              </h1>
              <p className="text-white/30 text-[11px] mt-0.5 ml-[26px]">Importér bankudtog, find abonnementer og besparelser</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="export-excel-btn" onClick={downloadExcel} className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/[0.06] rounded-lg flex items-center gap-1.5 transition-colors">
              <Download className="w-3.5 h-3.5" /> Excel
            </button>
            <button data-testid="refresh-btn" onClick={() => { loadDashboard(); loadTransactions(); }} className="px-3 py-1.5 text-xs text-white/60 hover:text-white hover:bg-white/[0.06] rounded-lg flex items-center gap-1.5 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Opdater
            </button>
          </div>
        </div>
      </header>

      <div className="p-3 md:p-5 space-y-4">
        <UploadZone onUploaded={() => { loadDashboard(); if (tab === 'transactions') loadTransactions(); }} />

        {/* Tabs */}
        <div className="flex items-center gap-0.5 bg-white/[0.04] rounded-lg p-0.5 w-fit" data-testid="expense-tabs">
          {TABS.map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${tab === t.key ? 'bg-[#1E6F7A] text-white shadow-lg shadow-[#1E6F7A]/20' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {tab === 'dashboard' && dashboard && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard label="Aktive abonnementer" value={dashboard.active_subscriptions} icon={BadgeCheck} accent="#1E6F7A" />
              <KpiCard label="Måned (abon.)" value={fmtAmountShort(dashboard.monthly_subscription_total) + ' kr'} sublabel={fmtAmount(dashboard.monthly_subscription_total) + ' kr'} accent="#3b82f6" icon={TrendingUp} />
              <KpiCard label="År (abon.)" value={fmtAmountShort(dashboard.yearly_subscription_total) + ' kr'} sublabel={fmtAmount(dashboard.yearly_subscription_total) + ' kr'} accent="#3b82f6" icon={TrendingUp} />
              <KpiCard label="Potentielle besparelser" value={fmtAmountShort(dashboard.potential_savings) + ' kr'} sublabel={fmtAmount(dashboard.potential_savings) + ' kr'} accent="#10b981" icon={Sparkles} />
              <KpiCard label="Transaktioner" value={dashboard.transaction_count} accent="#a78bfa" icon={FileSpreadsheet} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-5">
                <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-3">Top leverandører</h3>
                <div className="space-y-1">
                  {dashboard.top_vendors.slice(0, 10).map((v, i) => {
                    const dc = v.decision ? DECISION_COLORS[v.decision] : null;
                    return (
                      <div key={v.vendor} className="flex items-center justify-between text-xs py-1.5 border-b border-white/[0.04] last:border-0">
                        <span className="text-white/40 w-5">{i + 1}.</span>
                        <span className="text-white/80 flex-1 truncate ml-2" title={v.vendor}>{v.vendor}</span>
                        {v.decision && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded mr-2 ${dc.bg} ${dc.text}`}>{v.decision}</span>
                        )}
                        <span className="text-white/40 mr-3">{v.count}x</span>
                        <span className="text-rose-400/85 font-semibold tabular-nums">{fmtAmount(v.total)} kr</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-5">
                <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-3">Udgifter pr. kategori</h3>
                <div className="space-y-1">
                  {dashboard.category_breakdown.map(c => (
                    <div key={c.category} className="flex items-center justify-between text-xs py-1.5 border-b border-white/[0.04] last:border-0">
                      <span className="text-white/80 flex-1">{c.category}</span>
                      <span className="text-rose-400/85 font-semibold tabular-nums">{fmtAmount(c.total)} kr</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-5">
              <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold mb-3">Månedlig udvikling</h3>
              <div className="flex items-end gap-1.5 h-32">
                {dashboard.monthly_trend.map(m => {
                  const max = Math.max(...dashboard.monthly_trend.map(x => x.total), 1);
                  const h = (m.total / max) * 100;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 group">
                      <div className="text-[9px] text-white/40 opacity-0 group-hover:opacity-100 transition-opacity">{fmtAmountShort(m.total)}</div>
                      <div className="w-full bg-rose-500/40 hover:bg-rose-500/70 rounded-t transition-colors" style={{ height: `${h}%` }} />
                      <span className="text-[9px] text-white/30">{m.month.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* TRANSACTIONS */}
        {tab === 'transactions' && (
          <div className="space-y-3">
            <FilterBar filters={filters} setFilters={setFilters} categories={categories} responsibles={responsibles} vendors={vendorList} />

            <div className="bg-[#13151a] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="grid items-center text-[10px] uppercase tracking-wider text-white/35 font-semibold bg-white/[0.02] px-0 py-2.5"
                style={{ gridTemplateColumns: '100px 1fr 180px 120px 110px 130px 160px 30px' }}>
                <div className="px-3">Dato</div>
                <div className="px-3">Tekst</div>
                <div className="px-3">Leverandør</div>
                <div className="px-3">Kategori</div>
                <div className="px-3 text-right">Beløb</div>
                <div className="px-3">Handling</div>
                <div className="px-3">Ansvarlig</div>
                <div></div>
              </div>
              <div className="max-h-[60vh] overflow-y-auto" data-testid="tx-list">
                {txs.length === 0 && !loading && <div className="p-6 text-center text-white/40 text-sm">Ingen transaktioner. Importér en CSV-fil ovenfor.</div>}
                {txs.map(t => <TxRow key={t.id} tx={t} categories={categories} responsibles={responsibles} onChange={(p) => handleTxUpdate(t.id, p)} />)}
              </div>
              <div className="px-4 py-2 border-t border-white/[0.06] text-[11px] text-white/40">{txs.length} transaktioner</div>
            </div>
          </div>
        )}

        {/* SUBSCRIPTIONS */}
        {tab === 'subscriptions' && dashboard && (
          <SubscriptionsPanel dashboard={dashboard} responsibles={responsibles} onChange={loadDashboard} />
        )}

        {/* DUPLICATES */}
        {tab === 'duplicates' && dashboard && (
          <div className="bg-[#13151a] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/[0.06]">
              <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold">Mulige dubletter</h3>
              <p className="text-[11px] text-white/35 mt-1">Samme leverandør, samme beløb, indenfor 3 dage</p>
            </div>
            {dashboard.duplicates.length === 0 ? (
              <div className="p-6 text-center text-white/40 text-sm">Ingen dubletter fundet 🎯</div>
            ) : (
              <div className="divide-y divide-white/[0.04]" data-testid="dupes-list">
                {dashboard.duplicates.map((d, i) => (
                  <div key={i} className="px-5 py-3 hover:bg-white/[0.02]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white/85">{d.vendor}</span>
                      <span className="text-rose-400/85 font-semibold tabular-nums text-sm">{fmtAmount(d.amount)} kr</span>
                    </div>
                    <div className="text-[11px] text-white/45">{d.date_1} & {d.date_2}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI */}
        {tab === 'ai' && (
          <div className="space-y-4">
            <div className="bg-[#13151a] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-semibold">AI-analyse</h3>
                  <p className="text-[11px] text-white/35 mt-1">Claude Sonnet 4.5 finder ubrugte abonnementer, dubletter, prisspring og besparelser</p>
                </div>
                <button data-testid="run-ai-btn" onClick={runAi} disabled={aiBusy}
                  className="px-4 py-2 text-xs font-medium bg-[#1E6F7A] text-white rounded-lg hover:bg-[#1E6F7A]/80 disabled:opacity-50 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  {aiBusy ? 'Analyserer…' : 'Kør analyse'}
                </button>
              </div>
              {aiResult && aiResult.ok && (
                <div className="space-y-4 mt-4">
                  {aiResult.analysis.summary && (
                    <div className="bg-[#1E6F7A]/10 border border-[#1E6F7A]/30 rounded-lg p-4">
                      <p className="text-sm text-white/90">{aiResult.analysis.summary}</p>
                    </div>
                  )}
                  {(aiResult.analysis.likely_unused || []).length > 0 && (
                    <AiSection title="🟡 Sandsynligt ubrugte abonnementer" items={aiResult.analysis.likely_unused} render={(it) => <><strong className="text-white/90">{it.vendor}</strong> — {it.reason}</>} />
                  )}
                  {(aiResult.analysis.reduce_opportunities || []).length > 0 && (
                    <AiSection title="💸 Besparelsesmuligheder" items={aiResult.analysis.reduce_opportunities} render={(it) => <><strong className="text-white/90">{it.vendor}</strong> ({fmtAmount(it.current_cost)} kr) — {it.suggestion}</>} />
                  )}
                  {(aiResult.analysis.possible_duplicate_vendors || []).length > 0 && (
                    <AiSection title="🔁 Muligvis samme leverandør" items={aiResult.analysis.possible_duplicate_vendors} render={(it) => <><strong className="text-white/90">{(it.vendors || []).join(' = ')}</strong> — {it.reason}</>} />
                  )}
                  {(aiResult.analysis.abnormal_payments || []).length > 0 && (
                    <AiSection title="⚠️ Unormale betalinger" items={aiResult.analysis.abnormal_payments} render={(it) => <><strong className="text-white/90">{it.vendor}</strong> — {it.reason}</>} />
                  )}
                </div>
              )}
              {aiResult && !aiResult.ok && <p className="text-rose-400 text-sm mt-3">{aiResult.error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const AiSection = ({ title, items, render }) => (
  <div>
    <h4 className="text-[11px] uppercase tracking-wider text-white/50 font-semibold mb-2">{title}</h4>
    <ul className="space-y-1.5">
      {items.map((it, i) => <li key={i} className="text-xs text-white/70 leading-relaxed pl-1">• {render(it)}</li>)}
    </ul>
  </div>
);
